import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';

import Reviews, { Review } from '../../../../components/reviews/reviews';

import { SUBMIT_REVIEW_FEEDBACK } from '../../../../shared/graphql/review-mutations';
import { GET_STORE_REVIEWS } from '../../graphql/store-detail-queries';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { localStorage as ls } from '../../../../shared/utils/storage/storage';
import { getSortOptions } from '../../../../shared/utils/store-reviews/store-reviews';

function StoreReviews(props) {
  const { storeCode } = props;
  const [reviewQuery, setReviewQuery] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [sortOptions, setSortOptions] = useState([]);
  const [selectedSort, setSelectedSort] = useState(null);
  const [storeReviews, setStoreReviews] = useState([]);
  const [feedbackIds, setFeedbackIds] = useState(getReviewFeedbackIds);
  const prevPageNumberRef = useRef(pageNumber);
  const { data, loading: isLoading } = useQuery(GET_STORE_REVIEWS, {
    variables: {
      page: pageNumber,
      q: reviewQuery,
      storeCode
    }
  });

  useEffect(() => {
    const oldStoreCode = data?.store?.byCode?.code;
    const newStoreCode = storeCode;
    if (oldStoreCode && newStoreCode && oldStoreCode !== newStoreCode) {
      setReviewQuery('');
      setPageNumber(0);
      setSelectedFilter({});
      setSortOptions([]);
      setSelectedSort(null);
      setStoreReviews([]);
      setFeedbackIds(getReviewFeedbackIds);
      prevPageNumberRef.current = 0;
    }
  }, [data, storeCode]);

  const [submitFeedback] = useMutation(SUBMIT_REVIEW_FEEDBACK);
  const { pagination, results: reviews } = data?.store?.reviews || {};
  const filters = useMemo(getFilters, [data?.store?.reviews?.facets]);
  let showLoadMore = false;

  function getReviewFeedbackIds() {
    return ls('storeReviews.feedbackIds') || [];
  }

  function getFilters() {
    let filterOptions = [];
    const rawOptions = data?.store?.reviews?.facets?.[0]?.values;

    if (Array.isArray(rawOptions)) {
      filterOptions = ['5.0', '4.0', '3.0', '2.0', '1.0'].map(f => {
        const rawOption = rawOptions.find(o => o?.code === f);

        return {
          label: `${parseInt(f)} Stars`,
          selectable: parseInt(rawOption?.count) > 0,
          value: rawOption?.code
        };
      });
    }

    return [
      {
        code: 'filter',
        label: 'Filter',
        options: [{ label: 'All Ratings', value: 'all' }, ...filterOptions]
      }
    ];
  }

  function submitReviewFeedback(reviewId, isHelpful) {
    submitFeedback({
      variables: {
        isHelpful,
        reviewId
      }
    });

    if (!feedbackIds.includes(reviewId)) {
      const ids = [...feedbackIds, reviewId];

      setFeedbackIds(ids);
      ls('storeReviews.feedbackIds', ids);
    }
  }

  function updateSelection(sortOption, filterOption) {
    let query = '';
    const filter = filterOption ? filterOption : selectedFilter;
    const sort = sortOption ? sortOption : selectedSort;

    if (sort?.value) query += `:${sortOption?.value}`;

    if (typeof filter === 'object') {
      for (const key in filter) {
        const filterValue = filter[key]?.value;
        if (filterValue && filterValue !== 'all') {
          query += `:averageRating:${filterValue}`;
        }
      }
    }

    prevPageNumberRef.current = 0;

    setSelectedFilter(filterOption);
    setSelectedSort(sort);
    setPageNumber(0);
    setReviewQuery(query);
  }

  function onFilterChange(filterOption) {
    let sortOption = selectedSort;

    if (
      (filterOption.label !== 'All Ratings' &&
        selectedSort.label === 'Highest Rated') ||
      selectedSort.label === 'Lowest Rated'
    ) {
      sortOption = {
        label: 'Most Recent',
        value: 'most-recent-sr'
      };
    }

    updateSelection(sortOption, { filter: { ...filterOption } });
  }

  function onSortChange(sortOption) {
    let filterOption = selectedFilter;

    if (sortOption.label.includes('Rated')) {
      const selectedOption = filters[0]?.options?.[0];

      if (selectedOption) filterOption = { filter: selectedOption };
    }
    updateSelection(sortOption, filterOption);
  }

  function onLoadMore() {
    setPageNumber(page => page + 1);
  }

  if (
    pagination &&
    typeof pagination.currentPage === 'number' &&
    typeof pagination.numberOfPages === 'number' &&
    pagination.currentPage + 1 < pagination.numberOfPages
  ) {
    showLoadMore = true;
  }

  useEffect(() => {
    if (
      Array.isArray(filters) &&
      filters.length > 0 &&
      !selectedFilter.filter
    ) {
      const selectedOption = filters[0]?.options?.[0];

      if (selectedOption) setSelectedFilter({ filter: selectedOption });
    }
  }, [filters, selectedFilter]);

  useEffect(() => {
    if (
      Array.isArray(data?.store?.reviews?.sorts) &&
      data.store?.reviews.sorts.length > 0
    ) {
      const sortOptions = getSortOptions(
        data.store?.reviews.sorts,
        selectedFilter
      );
      setSortOptions(sortOptions);

      if (!selectedSort) {
        setSelectedSort(sortOptions[0]);
      }
    }
  }, [data, selectedFilter, selectedSort]);

  useEffect(() => {
    if (pageNumber > 0) {
      if (
        !isLoading &&
        Array.isArray(reviews) &&
        prevPageNumberRef.current !== pageNumber
      ) {
        prevPageNumberRef.current = pageNumber;

        setStoreReviews([...storeReviews, ...reviews]);
      }
    } else if (!isLoading && Array.isArray(reviews)) {
      setStoreReviews(reviews);
    }
  }, [reviews, pageNumber, storeReviews, isLoading]);

  useEffect(() => {
    trackAction('store_reviews');
  }, []);

  return (
    <>
      {Array.isArray(storeReviews) && storeReviews.length > 0 && (
        <Reviews
          filters={filters}
          onFilterChange={onFilterChange}
          onLoadMore={onLoadMore}
          onSortChange={onSortChange}
          selectedFilters={selectedFilter}
          selectedSort={selectedSort}
          showLoadMore={showLoadMore}
          sortOptions={sortOptions}
        >
          {storeReviews.map((review, idx) => {
            const hasFeedbackSubmitted = feedbackIds.includes(review.id);
            let answers = [];
            const answerMap = review?.answers?.reduce((acc, answer) => {
              if (answer.key && answer.value) {
                return {
                  ...acc,
                  [answer.key]: answer.value
                };
              }
              return null;
            }, {});

            if (answerMap.knowledge && answerMap.cleanliness) {
              const { knowledge, cleanliness } = answerMap;

              answers = [
                {
                  label: 'Employee Knowledge/ Friendliness',
                  rating: Number(knowledge)
                },
                { label: 'Store Cleanliness', rating: Number(cleanliness) }
              ];
            }

            return (
              <Review
                answers={answers}
                comments={review.reviewText}
                date={review.submissionDate}
                heading={review.userDisplayName}
                helpfulCount={review.helpfulCount}
                id={review.id}
                isFeedbackSubmitted={hasFeedbackSubmitted}
                isRecommended={review.recommended}
                key={`sr-${idx}`}
                notHelpfulCount={review.notHelpfulCount}
                notRecommendedMessage="I wouldn't recommend this store"
                onHelpfulClick={submitReviewFeedback}
                rating={review.averageRating}
                recommendedMessage="Yes, I would recommend this store"
              />
            );
          })}
        </Reviews>
      )}
    </>
  );
}

StoreReviews.propTypes = {
  storeCode: PropTypes.string.isRequired
};

export default StoreReviews;
